import React, { Component } from 'react'
import { withRouter } from 'react-router-dom'
import CacheKeys from '../../../cache/CacheKeys'
import CacheManager from '../../../cache/CacheManager'
import RestApiUtils from '../../../apiclient/moebli/RestApiUtils'
import WhatsAppChatPageUI from './WhatsAppChatPageUI.js'

class WhatsAppChatPageSF extends Component {
    constructor(props) {
        super(props)
        this.state = {
            account: null,
            errorList: [],
            loading: true,
            conversations: [],
            selectedUserIndex: 0,
            messages: [],
            messageText: '',
            selectedFile: null,
            uploadedFilePath: null,
            isUploading: false,
            uploadError: null
        }
    }

    componentDidMount() {
        let context = this.props.context
        this.getAccount(context)
        this.getWhatsAppConversations(context)
    }

    getAccount(context) {
        let cacheManager = new CacheManager({
            resource: `/api/su/accounts/${this.props.match.params.accountId}`,
            cacheKey: CacheKeys.Accounts(this.props.match.params.accountId),
            callback: this.getAccountCallback,
            ttlInSecs: 600
        })
        cacheManager.getObject2(context)
    }

    getAccountCallback = (response) => {
        this.setState({
            account: response.account,
            loading: this.state.conversations.length === 0 // Keep loading true if conversations not loaded yet
        })
    }

    getWhatsAppConversations = (context) => {
        RestApiUtils.get(
            context,
            // `/api/whatsapp-conversations/account/7e89a063-8fc4-4fde-9c39-b9136df53320`,
            `/api/whatsapp-conversations/account/${this.props.match.params.accountId}`,
            this.getWhatsAppConversationsCallback
        )
    }

    getWhatsAppConversationsCallback = (response) => {
        if (response.errorList && response.errorList.length > 0) {
            this.setState({
                errorList: response.errorList,
                loading: false
            })
            return
        }

        const conversations = response.whatsAppConversationsGrouped || []

        // Process messages for the first user by default
        let messages = conversations.length > 0 ?
            this.processMessages(conversations[0]) : []

        // Sort messages by timestamp (oldest first)
        messages = this.sortMessagesByTimestamp(messages)

        this.setState({
            conversations,
            messages,
            loading: false
        })
    }

    // Process messages from raw conversation data
    // Fetch media content and get signed URL
    fetchMediaContent = (path, trackerId, callback) => {
        if (!path) {
            console.error('No media path provided for message:', trackerId)
            callback(null, path, trackerId)
            return
        }

        console.log(`Fetching media for message ${trackerId}, path: ${path}`)

        const payload = {
            path: path
        }

        RestApiUtils.post(
            this.props.context,
            '/api/s3/preview',
            (response) => {
                console.log(`Media API response for message ${trackerId}:`, response)
                if (response && response.success === 'success' && response.signedUrl) {
                    console.log(`Got signed URL for message ${trackerId}:`, response.signedUrl)
                    callback(response.signedUrl, response.path, trackerId)
                } else {
                    console.error(`Failed to get media preview URL for message ${trackerId}`, response)
                    callback(null, path, trackerId)
                }
            },
            payload
        )
    }

    processMessages = (conversationGroup) => {
        if (!conversationGroup || !conversationGroup.conversation) return []

        // Process all messages from the conversation
        const processedMessages = conversationGroup.conversation.map((message, index) => {
            // Create a unique trackerId for each message
            const trackerId = message.id || `${conversationGroup.user_id}_${Date.now()}_${index}`

            // Format timestamp consistently DD/MM/YYYY hh:mm AM/PM format
            const date = new Date(message.timestamp)
            const formattedDate = date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            })
            const formattedTime = date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            })

            // Create the formatted message object
            const formattedMessage = {
                id: message.id,
                trackerId: trackerId,
                timestamp: `${formattedDate} ${formattedTime}`,
                sender: message.direction === 'OUTBOUND' ? 'company' : 'user',
                senderName: message.direction === 'OUTBOUND' ? 'Company' : conversationGroup.userName,
                senderPhone: message.direction === 'OUTBOUND' ? '' : conversationGroup.whatsAppConversationsPhoneNumber,
                text: message.message || null
            }

            // Handle media content
            if (message.media && message.media.type) {
                formattedMessage.type = message.media.type
                formattedMessage.mediaPath = message.media.url
            } else if (message.mediaUrl) {
                formattedMessage.type = this.determineMediaType(message.mediaUrl)
                formattedMessage.mediaPath = message.mediaUrl
            } else if (message.mediaList && message.mediaList.length > 0) {
                const media = message.mediaList[0]
                formattedMessage.type = this.determineMediaType(media.url)
                formattedMessage.mediaPath = media.url
            }

            return formattedMessage
        })

        // Sort all messages by timestamp
        return this.sortMessagesByTimestamp(processedMessages)
    }

    // Helper function to parse various timestamp formats to a Date object
    parseTimestamp = (timestamp) => {
        if (!timestamp) return new Date(0);

        try {
            // Handle different timestamp formats

            // Format: "DD/MM/YYYY hh:mm AM/PM" (e.g., "22/05/2025 04:12 PM")
            if (timestamp.includes('/') && (timestamp.includes('AM') || timestamp.includes('PM'))) {
                const [datePart, timePart, ampmPart] = timestamp.split(' ');
                const [day, month, year] = datePart.split('/').map(num => parseInt(num, 10));

                // Parse time (handle both "hh:mm AM/PM" and cases where AM/PM is separate)
                let hours, minutes, ampm;

                if (ampmPart) {
                    // Case: time and AM/PM are separate parts
                    [hours, minutes] = timePart.split(':').map(num => parseInt(num, 10));
                    ampm = ampmPart;
                } else {
                    // Case: AM/PM is part of the time string
                    let timeWithAmPm = timePart;
                    ampm = timeWithAmPm.includes('AM') ? 'AM' : 'PM';
                    timeWithAmPm = timeWithAmPm.replace('AM', '').replace('PM', '').trim();
                    [hours, minutes] = timeWithAmPm.split(':').map(num => parseInt(num, 10));
                }

                // Convert 12-hour format to 24-hour
                if (ampm.toUpperCase() === 'PM' && hours < 12) hours += 12;
                if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;

                // JavaScript months are 0-indexed (0 = January, 11 = December)
                return new Date(year, month - 1, day, hours, minutes);
            }

            // Default case: try standard JavaScript Date parsing
            return new Date(timestamp);
        } catch (e) {
            console.error('Error parsing timestamp:', timestamp, e);
            return new Date(0); // Return epoch time for invalid dates
        }
    }

    // Helper function to sort messages by timestamp
    sortMessagesByTimestamp = (messages) => {
        if (!messages || !messages.length) return [];

        return [...messages].sort((a, b) => {
            // Parse timestamp strings using custom parser
            const dateA = this.parseTimestamp(a.timestamp);
            const dateB = this.parseTimestamp(b.timestamp);

            // Sort in ascending order (oldest first)
            return dateA - dateB;
        });
    }

    // Handle user selection
    handleUserSelect = (index) => {
        const { conversations } = this.state
        if (conversations.length <= index) return

        // Process messages for the selected user
        let messages = this.processMessages(conversations[index])

        // Sort messages by timestamp
        messages = this.sortMessagesByTimestamp(messages)

        this.setState({
            selectedUserIndex: index,
            messages,
            messageText: '',
            uploadedFilePath: null,
            selectedFile: null
        })
    }

    // Determine media type based on file extension
    determineMediaType = (path) => {
        if (!path) return 'unknown'

        const extension = path.split('.').pop().toLowerCase()

        // Image formats
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
            return 'image'
        }

        // Video formats
        if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'wmv', 'flv', 'mkv'].includes(extension)) {
            return 'video'
        }

        // Audio formats
        if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(extension)) {
            return 'audio'
        }

        // Document formats
        if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(extension)) {
            return 'document'
        }

        return 'file' // Default type for other formats
    }

    // For fallback/demo purposes if the API isn't available
    getMockMessages = () => {
        if (this.state.conversations.length > 0) {
            return this.state.messages
        }

        // Return mock data if no API data is available
        return [
            {
                id: 2,
                trackerId: 'mock_2',
                text: "Start a conversation",
                timestamp: "14-05-2025 06:15 PM",
                sender: "company",
                companyName: "Archescent, Inc."
            },
        ]
    }

    render() {
        const { account, loading, conversations, selectedUserIndex, messageText, isUploading, uploadError } = this.state

        return (
            <WhatsAppChatPageUI
                context={this.props.context}
                account={account}
                loading={loading}
                errorList={this.state.errorList}
                messages={this.getMockMessages()}
                conversations={conversations}
                selectedUserIndex={selectedUserIndex}
                onUserSelect={this.handleUserSelect}
                onFileSelect={this.handleFileSelect}
                onMessageChange={this.handleMessageChange}
                onSendMessage={this.handleSendMessage}
                messageText={messageText}
                isUploading={isUploading}
                uploadError={uploadError}
                selectedFile={this.state.selectedFile}
                fetchMediaContent={this.fetchMediaContent}
            />
        )
    }

    handleMessageChange = (e) => {
        this.setState({ messageText: e.target.value })
    }

    handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0]
            this.setState({
                selectedFile: file,
                uploadedFilePath: null,
                uploadError: null
            })
            this.uploadFile(file)
        }
    }

    uploadFile = (file) => {
        this.setState({ isUploading: true, uploadError: null })

        console.log(this.state.conversations)
        console.log(this.state.selectedUserIndex)
        console.log(this.state.conversations[this.state.selectedUserIndex])
        console.log(this.state.conversations[this.state.selectedUserIndex].user_id)
        // First get a signed URL from the API
        const userId = this.state.conversations[this.state.selectedUserIndex].user_id // Hardcoded for now, can be dynamic later
        const fileName = file.name

        RestApiUtils.get(
            this.props.context,
            `/api/s3/fileUploadSignedUrl/${userId}/${fileName}`,
            this.handleSignedUrlResponse,
            this.handleUploadError
        )
    }

    handleSignedUrlResponse = (response) => {
        if (response && response.success === 'success' && response.signedUrl) {
            const { signedUrl, path } = response
            // Now upload the file to the signed URL
            this.setState({
                uploadedFilePath: path
            })
            this.uploadFileToS3(signedUrl, path)
        } else {
            this.setState({
                isUploading: false,
                uploadError: 'Failed to get upload URL'
            })
        }
    }

    uploadFileToS3 = (signedUrl, path) => {
        const { selectedFile } = this.state
        console.log('Uploading file to S3:', selectedFile)
        if (!selectedFile) {
            this.setState({ isUploading: false })
            return
        }

        // Create a FileReader to read the file as an ArrayBuffer
        const reader = new FileReader()
        reader.onload = (event) => {
            console.log('File read successfully:', signedUrl, path, event.target.result, selectedFile.type)
            // Once the file is read, send it to S3
            this.uploadFileV2(signedUrl, selectedFile)
            // this.sendFileToS3(signedUrl, path, event.target.result, selectedFile.type)
        }
        reader.onerror = () => {
            console.error('Error reading file')
            this.handleUploadError('Error reading file')
        }

        // Read the file as an ArrayBuffer
        reader.readAsArrayBuffer(selectedFile)
    }

    // sendFileToS3 = (signedUrl, path, fileData, fileType) => {
    //     console.log('Sending file to S3:', signedUrl, path, fileData, fileType)
    //     // Create a PUT request to the signed URL
    //     const xhr = new XMLHttpRequest()
    //     xhr.open('PUT', signedUrl, true)

    //     // Set the content type header based on file type
    //     // This is critical for S3 to accept and properly store the file
    //     xhr.setRequestHeader('Content-Type', fileType || 'application/octet-stream')

    //     console.log('Uploading file with Content-Type:', fileType || 'application/octet-stream')

    //     xhr.onload = () => {
    //         if (xhr.status === 200) {
    //             console.log('File uploaded successfully to:', path)
    //             // File uploaded successfully
    //             this.setState({ 
    //                 isUploading: false, 
    //                 uploadedFilePath: path 
    //             })
    //         } else {
    //             console.error('Upload failed with status:', xhr.status, xhr.statusText, xhr.response)
    //             this.handleUploadError(`Failed to upload file: ${xhr.status} ${xhr.statusText}`)
    //         }
    //     }
    //     xhr.onerror = (error) => {
    //         console.error('Upload error:', error)
    //         this.handleUploadError('Network error during upload')
    //     }
    //     // xhr.upload.onprogress = (e) => {
    //     //     if (e.lengthComputable) {
    //     //         const percentComplete = Math.round((e.loaded / e.total) * 100)
    //     //         console.log(`Upload progress: ${percentComplete}%`)
    //     //         // Could update UI with progress if needed
    //     //     }
    //     // }

    //     // Send the file data as binary
    //     xhr.send(fileData)
    // }

    uploadFileV2 = (signedUrl, file) => {
        let putResource = signedUrl
        console.log('-> PUT', putResource)
        fetch(putResource, {
            method: 'put',
            body: file,
        })
            .then((response) => {
                console.log('<- PUT', putResource)
                console.log(response.status)
                this.setState({
                    isUploading: false,
                })
            }).catch((error) => {
                console.error('Upload error:', error)
                this.handleUploadError('Failed to upload file')
            })
    }

    handleUploadError = (error) => {
        console.error('Upload error:', error)
        this.setState({
            isUploading: false,
            uploadError: typeof error === 'string' ? error : 'Failed to upload file'
        })
    }

    handleSendMessage = () => {
        const { messageText, uploadedFilePath, conversations, selectedUserIndex } = this.state

        if (!messageText && !uploadedFilePath) {
            console.log("No message or file selected")
            return // Don't send empty messages
        }

        // Create request payload
        console.log("conversations", conversations)
        const payload = {
            userId: conversations[selectedUserIndex].user_id, // Hardcoded as per requirements
            message: messageText,
            filePath: uploadedFilePath || null
        }

        this.setState({ isSending: true })

        RestApiUtils.post(
            this.props.context,
            '/api/whatsapp-conversations/send-message',
            this.handleSendMessageSuccess,
            // this.handleSendMessageError
            payload,
        )
    }

    handleSendMessageSuccess = (response) => {
        // Clear form and show success
        this.setState({
            messageText: '',
            selectedFile: null,
            uploadedFilePath: null,
            isSending: false
        })

        // Refresh the conversation
        this.getWhatsAppConversations(this.props.context)
    }

    handleSendMessageError = (error) => {
        console.error('Error sending message:', error)
        this.setState({
            isSending: false,
            sendError: 'Failed to send message. Please try again.'
        })
    }
}
export default withRouter(WhatsAppChatPageSF)
